/* __next_internal_client_entry_do_not_use__ Logo,default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
// Local
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { RemixIcon } from 'helpers/RemixIcon';
import { SvgIcon } from 'helpers/SvgIcon';
import useClickOutside from 'lib/hooks/useClickOutside';
import { useIsScrolled } from 'lib/hooks/useIsScrolled';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { useHeader } from './HeaderContext';
import useDictionary from 'lib/hooks/useDictionary';
import { getTestProps } from 'lib/testing/utils';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import GlobalSearchInput from 'components/authorable/shared/search/GlobalSearchInput';
import { sendGTMEvent } from '@next/third-parties/google';
import { createCTAClickGTMEvent, createNavClickGTMEvent } from 'lib/utils/gtm-utils';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
const getColumnGroupHeading = (group)=>{
    const link = group?.megaMenuLink?.jsonValue;
    const label = group?.megaMenuLabel?.jsonValue?.value?.trim();
    return {
        text: label || link?.value?.text?.trim() || '',
        link: link?.value?.href ? link : undefined
    };
};
/**
 * Whether a tier 2 category has a panel behind it. Counts the links rather than the
 * entries, because an empty column group is still an entry. One predicate for both sides:
 * the row hides its arrow on it, and the panel draws nothing on it — otherwise a childless
 * category puts a heading and a rule over empty space.
 */ const hasTier3Links = (category)=>(category.megaMenuLinks?.targetItems ?? []).some((entry)=>{
        const group = entry;
        const nested = group.megaMenuCategory?.targetItems;
        if (nested) return nested.length > 0;
        return !!entry.link?.jsonValue?.value?.href;
    });
const isPointInsideOpenMegaMenu = (x, y)=>Array.from(document.querySelectorAll('[data-mega-menu="true"]')).filter((panel)=>panel.offsetParent !== null).some((panel)=>{
        const rect = panel.getBoundingClientRect();
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    });
const isInsideOpenMegaMenu = (event)=>isPointInsideOpenMegaMenu(event.clientX, event.clientY);
let lastPointerDown = null;
if (typeof document !== 'undefined') {
    document.addEventListener('mousedown', (event)=>{
        lastPointerDown = {
            x: event.clientX,
            y: event.clientY
        };
    }, true);
}
const getSidebarItems = (from)=>Array.from(from.closest('[data-mega-menu="true"]')?.querySelectorAll('[data-mega-menu-sidebar-item]') ?? []);
// Two-tier navigation components
const SidebarNavigation = ({ navigationTitle, navigationLink, megaMenuList, activeSidebarItem, onSidebarItemClick, featureTitle, featureDescription })=>{
    const { sidebarWrapper, sidebarTitle, sidebarTitleLink, sidebarTitleText, sidebarDivider, sidebarList, sidebarScrollArea, sidebarItem, sidebarItemActive, sidebarItemIcon, sidebarItemDescription, sidebarItemDescriptionText, sidebarItemDivider, sidebarFeatureWrapper, sidebarFeatureDivider, sidebarFeatureTitle, sidebarLinks, sidebarLinksActive } = TAILWIND_VARIANTS();
    const showDividers = megaMenuList && megaMenuList.length >= 2;
    const hasTier1Link = navigationLink?.value?.href;
    const hasCategoryDescriptions = megaMenuList?.some((category)=>category.megaMenuDescription?.jsonValue?.value);
    const showTitle = !hasCategoryDescriptions;
    // Fix tab order: sidebar description and feature description links should come AFTER tier 3 content
    useEffect(()=>{
        // Item description links (e.g., "London", "Manchester", "Leeds")
        const itemDescriptionLinks = document.querySelectorAll('[data-mega-menu-description] a');
        itemDescriptionLinks.forEach((link)=>{
            link.setAttribute('tabindex', '1');
        });
        // Feature description links at bottom (e.g., "link text", "here")
        const featureDescriptionLinks = document.querySelectorAll('[data-mega-menu-feature-description] a');
        featureDescriptionLinks.forEach((link)=>{
            link.setAttribute('tabindex', '1');
        });
    }, [
        megaMenuList
    ]);
    /*
   * Opening a tier 3 panel from a category that has no link of its own. Nothing is
   * navigated to, so LinkWrapper never reports it and the dataLayer would otherwise miss
   * the interaction entirely; `link_url` is left empty for the same reason.
   */ const selectSidebarItem = (index, category)=>{
        sendGTMEvent(createNavClickGTMEvent({
            clickText: category.name,
            navLevel: 2,
            navLocation: 'mega_menu',
            menuCat1: navigationTitle?.value,
            menuCat2: category.name
        }));
        onSidebarItemClick(index);
    };
    const handleSidebarKeyDown = (event, index, category)=>{
        const categoryLink = category.megaMenuCTA?.jsonValue?.value?.href;
        const totalItems = megaMenuList?.length || 0;
        const hasTier3Content = (category.megaMenuLinks?.targetItems?.length ?? 0) > 0;
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            onSidebarItemClick(index);
            const row = event.currentTarget;
            setTimeout(()=>{
                const menu = row.closest('[data-mega-menu="true"]');
                if (!menu) return;
                // Sidebar rows and prose links are not panel content.
                const panelLinks = Array.from(menu.querySelectorAll('a[href]')).filter((link)=>!link.closest('[data-mega-menu-sidebar]'));
                panelLinks[0]?.focus();
            }, 150);
        } else if (event.key === 'Enter' || event.key === ' ') {
            // On Enter/Space: For links with tier 3 content, don't navigate - just show tier 3
            // For links without tier 3 content, allow default navigation
            if (categoryLink && hasTier3Content) {
                event.preventDefault();
                onSidebarItemClick(index);
            } else if (!categoryLink) {
                // No link - just show tier 3
                event.preventDefault();
                onSidebarItemClick(index);
            }
        // If has link but no tier 3 - allow default navigation
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            // Wrap around the tier 2 list.
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = (index + step + totalItems) % totalItems;
            getSidebarItems(event.currentTarget)[nextIndex]?.focus();
        }
    };
    const handleSidebarFocus = (index)=>{
        // Show tier 3 when sidebar item receives focus
        onSidebarItemClick(index);
    };
    return (
        /* Marks the whole tier 2 column, so panel-only lookups can exclude it wholesale —
    the title link and the help text at the foot live here too, not just the rows. */ /*#__PURE__*/ (_jsxs("div", {
            className: sidebarWrapper(),
            "data-mega-menu-sidebar": true,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: sidebarTitle(),
                    children: [
                        showTitle && hasTier1Link && /*#__PURE__*/ _jsx(LinkWrapper, {
                            ctaComponentClass: "default",
                            ctaVariant: "custom",
                            className: sidebarTitleLink(),
                            field: navigationLink,
                            suppressNewTabIcon: true,
                            gtmEvent: createNavClickGTMEvent({
                                clickText: navigationTitle?.value,
                                navLevel: 1,
                                navLocation: 'mega_menu',
                                menuCat1: navigationTitle?.value,
                                linkUrl: hasTier1Link
                            }),
                            ...getTestProps(`header-mega-menu-title-link`),
                            children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                field: navigationTitle
                            })
                        }),
                        showTitle && !hasTier1Link && /*#__PURE__*/ _jsx("span", {
                            className: sidebarTitleText(),
                            ...getTestProps(`header-mega-menu-title-text`),
                            children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                field: navigationTitle
                            })
                        }),
                        showTitle && /*#__PURE__*/ _jsx("div", {
                            className: sidebarDivider()
                        }),
                        !showTitle && /*#__PURE__*/ _jsx("div", {
                            className: "h-[22px]"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: sidebarScrollArea(),
                    children: [
                        /*#__PURE__*/ _jsx("ul", {
                            className: `${sidebarList()} ${!(featureTitle?.value || featureDescription?.value) ? 'pb-spacing-spacing-32' : ''}`,
                            children: megaMenuList?.map((category, index)=>{
                                const hasDescription = !!category.megaMenuDescription?.jsonValue?.value;
                                const hasTier3 = hasTier3Links(category);
                                const categoryLink = category.megaMenuCTA?.jsonValue;
                                const categoryHref = categoryLink?.value?.href;
                                const isLinkRow = !hasTier3 && !!categoryHref;
                                const rowClassName = `${sidebarItem()} ${hasDescription ? '!flex-col !items-start' : ''} ${activeSidebarItem === index ? sidebarItemActive() : ''} group`;
                                const rowLabel = category.megaMenuTitle?.jsonValue?.value || category.name;
                                const rowAttributes = {
                                    'data-mega-menu-sidebar-item': '',
                                    ...(activeSidebarItem === index ? {
                                        'data-mega-menu-sidebar-selected': ''
                                    } : {})
                                };
                                /* Same innards either way, so the two rows are indistinguishable to look at. */ const rowContent = /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "flex w-full items-center justify-between",
                                            children: [
                                                rowLabel,
                                                hasTier3 && /*#__PURE__*/ _jsx(RemixIcon, {
                                                    className: `${sidebarItemIcon()} ${activeSidebarItem === index ? 'opacity-100 !text-component-header-mega-menu-link-icon-hover' : ''}`,
                                                    name: "arrow-right-line",
                                                    size: "m"
                                                })
                                            ]
                                        }),
                                        hasDescription && /*
                     * `tag="span"` keeps this phrasing content: a <div> inside a <button> is
                     * invalid HTML. Authors should keep these descriptions plain text — a link
                     * in here would be unreachable, since it would nest inside the control.
                     */ /*#__PURE__*/ _jsx("span", {
                                            className: sidebarItemDescription(),
                                            "data-mega-menu-description": true,
                                            children: /*#__PURE__*/ _jsx(RichTextWrapper, {
                                                tag: "span",
                                                className: sidebarItemDescriptionText(),
                                                field: category.megaMenuDescription?.jsonValue
                                            })
                                        })
                                    ]
                                });
                                return /*#__PURE__*/ _jsxs(React.Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("li", {
                                            className: `${sidebarLinks()} ${activeSidebarItem === index ? sidebarLinksActive() : ''}`,
                                            onClick: isLinkRow ? undefined : ()=>selectSidebarItem(index, category),
                                            children: isLinkRow ? /*#__PURE__*/ _jsx(LinkWrapper, {
                                                ctaComponentClass: "default",
                                                ctaVariant: "custom",
                                                className: rowClassName,
                                                field: categoryLink,
                                                suppressNewTabIcon: true,
                                                "aria-label": rowLabel,
                                                onFocus: ()=>handleSidebarFocus(index),
                                                /* Keeps a link row inside the same up/down cycle as the buttons. */ onKeyDown: (event)=>handleSidebarKeyDown(event, index, category),
                                                ...rowAttributes,
                                                gtmEvent: createNavClickGTMEvent({
                                                    clickText: category.name,
                                                    navLevel: 2,
                                                    navLocation: 'mega_menu',
                                                    menuCat1: navigationTitle?.value,
                                                    menuCat2: category.name,
                                                    linkUrl: categoryHref
                                                }),
                                                ...getTestProps(`header-sidebar-item-${index}`),
                                                children: rowContent
                                            }) : /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                className: rowClassName,
                                                onClick: ()=>onSidebarItemClick(index),
                                                onFocus: ()=>handleSidebarFocus(index),
                                                onKeyDown: (event)=>handleSidebarKeyDown(event, index, category),
                                                "aria-label": rowLabel,
                                                "aria-expanded": activeSidebarItem === index,
                                                ...rowAttributes,
                                                ...getTestProps(`header-sidebar-item-${index}`),
                                                children: rowContent
                                            })
                                        }),
                                        showDividers && index < megaMenuList.length - 1 && category.megaMenuDescription?.jsonValue?.value && /*#__PURE__*/ _jsx("li", {
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: sidebarItemDivider()
                                            })
                                        })
                                    ]
                                }, category.name);
                            })
                        }),
                        (featureTitle?.value || featureDescription?.value) && /*#__PURE__*/ _jsxs("div", {
                            className: sidebarFeatureWrapper(),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: sidebarFeatureDivider()
                                }),
                                featureTitle?.value && /*#__PURE__*/ _jsx("div", {
                                    className: sidebarFeatureTitle(),
                                    children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        field: featureTitle
                                    })
                                }),
                                featureDescription?.value && /*#__PURE__*/ _jsx("div", {
                                    "data-mega-menu-feature-description": true,
                                    children: /*#__PURE__*/ _jsx(RichTextWrapper, {
                                        className: "rte text-color-icon-dark text-typography-font-size-body-medium [&_p]:!text-typography-font-size-body-medium    [&_p>a]:!text-typography-font-size-body-medium [&_p>a]:!text-color-icon-dark",
                                        field: featureDescription
                                    })
                                })
                            ]
                        })
                    ]
                })
            ]
        }))
    );
};
const MegaMenuContent = ({ megaMenuList, activeSidebarItem, showSidebar = true, navigationTitle, navigationLink })=>{
    const { isTier3Active } = useHeader();
    // Handler for tier 3 link keyboard navigation
    const handleTier3KeyDown = (event, categoryIndex)=>{
        if (event.key === 'ArrowLeft' && showSidebar) {
            // Arrow left to go back to the tier 2 row this panel belongs to.
            event.preventDefault();
            getSidebarItems(event.currentTarget)[categoryIndex]?.focus();
        } else if (event.key === 'Tab' && !event.shiftKey && showSidebar) {
            const current = event.currentTarget;
            const menu = current.closest('[data-mega-menu="true"]');
            if (!menu) return;
            const panelLinks = Array.from(menu.querySelectorAll('a[href]')).filter((link)=>!link.closest('[data-mega-menu-sidebar]'));
            if (panelLinks.indexOf(current) !== panelLinks.length - 1) return;
            const rows = getSidebarItems(current);
            const next = rows[categoryIndex + 1] ?? rows[0];
            if (next) {
                event.preventDefault();
                next.focus();
            }
        }
    };
    const { megaMenuWrapper, megaMenuDivider, megaMenuGrid, megaMenuCol, megaMenuColHeading, megaMenuColHeadingLink, megaMenuColItems, megaMenuColItemsLink } = TAILWIND_VARIANTS({
        showSidebar
    });
    const activeCategory = activeSidebarItem !== null ? megaMenuList[activeSidebarItem] : undefined;
    if (!activeCategory) {
        return null;
    }
    // A category with no links has no panel: no heading, no rule, no empty column grid.
    if (!hasTier3Links(activeCategory)) {
        return null;
    }
    /*
   * One tier 3 entry, shared by the row layouts and the grouped columns so the link
   * behaviour — active underline, missing-destination fallback, GTM — lives in one place.
   * `label` is passed in because the two structures differ on purpose: flat lists use the
   * Link field's text, the grouped ones use the item name.
   */ const renderLinkItem = (linkItem, label, groupTitle, testKey, key)=>{
        const linkHref = linkItem.link?.jsonValue?.value?.href;
        const isActive = isTier3Active(linkHref);
        return /*#__PURE__*/ _jsx("li", {
            children: !linkHref ? // No destination authored yet — render the label, not a dead anchor.
            /*#__PURE__*/ _jsx("span", {
                className: megaMenuColItemsLink(),
                children: /*#__PURE__*/ _jsx(NavLink, {
                    link: undefined,
                    name: label
                })
            }) : /*#__PURE__*/ _jsx(LinkWrapper, {
                ctaComponentClass: "default",
                className: `${megaMenuColItemsLink()} ${isActive ? 'underline' : ''}`,
                field: linkItem.link?.jsonValue,
                onClick: (e)=>{
                    e.preventDefault();
                    if (linkHref) {
                        window.location.href = linkHref;
                    }
                },
                suppressNewTabIcon: true,
                onKeyDown: (e)=>handleTier3KeyDown(e, activeSidebarItem),
                "aria-current": isActive ? 'page' : undefined,
                ...getTestProps(testKey),
                gtmEvent: createNavClickGTMEvent({
                    clickText: label,
                    navLevel: 3,
                    navLocation: 'mega_menu',
                    menuCat1: navigationTitle?.value,
                    menuCat2: groupTitle,
                    menuCat3: label,
                    linkUrl: linkHref
                }),
                children: /*#__PURE__*/ _jsx(NavLink, {
                    link: linkItem.link?.jsonValue,
                    name: label
                })
            })
        }, key);
    };
    const renderGroupHeading = (group, extraClass, testKey)=>{
        const { text, link } = getColumnGroupHeading(group);
        // No title authored means nothing to head, so the rule under it goes too rather than
        // leaving a bare blue line sitting over the links.
        if (!text) return null;
        return /*#__PURE__*/ _jsxs("div", {
            className: `${megaMenuColHeading()} ${extraClass}`.trim(),
            children: [
                link ? /*#__PURE__*/ _jsx(LinkWrapper, {
                    ctaComponentClass: "default",
                    ctaVariant: "custom",
                    className: megaMenuColHeadingLink(),
                    field: link,
                    suppressNewTabIcon: true,
                    ...getTestProps(testKey),
                    children: text
                }) : text,
                /*#__PURE__*/ _jsx("div", {
                    className: megaMenuDivider()
                })
            ]
        });
    };
    const megaMenuLinks = activeCategory.megaMenuLinks?.targetItems;
    // Check if megaMenuLinks contains column groups or direct links
    const firstItem = megaMenuLinks?.[0];
    const isColumnGroupStructure = firstItem?.megaMenuCategory !== undefined;
    // If it's a flat structure (direct links), divide links evenly across columns
    if (!isColumnGroupStructure && megaMenuLinks) {
        const directLinks = megaMenuLinks ?? [];
        const categoryTitle = showSidebar ? activeCategory.name : navigationTitle?.value;
        const categoryTitleLink = showSidebar ? activeCategory.megaMenuCTA?.jsonValue : navigationLink;
        const categoryTitleHref = categoryTitleLink?.value?.href;
        // Use 4 columns if no sidebar, 3 columns if sidebar is shown
        const numColumns = showSidebar ? 3 : 4;
        const linksPerColumn = Math.ceil(directLinks.length / numColumns);
        const columns = [];
        for(let i = 0; i < numColumns; i++){
            const startIndex = i * linksPerColumn;
            const endIndex = Math.min(startIndex + linksPerColumn, directLinks.length);
            columns.push(directLinks.slice(startIndex, endIndex));
        }
        return /*#__PURE__*/ _jsxs("div", {
            className: megaMenuWrapper(),
            children: [
                categoryTitle && /*#__PURE__*/ _jsxs("div", {
                    className: `${megaMenuColHeading()} pb-spacing-spacing-24`,
                    children: [
                        categoryTitleHref ? /*#__PURE__*/ _jsx(LinkWrapper, {
                            ctaComponentClass: "default",
                            ctaVariant: "custom",
                            className: megaMenuColHeadingLink(),
                            field: categoryTitleLink,
                            suppressNewTabIcon: true,
                            ...getTestProps(`header-mega-menu-content-heading-link`),
                            children: categoryTitle
                        }) : categoryTitle,
                        /*#__PURE__*/ _jsx("div", {
                            className: megaMenuDivider()
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: megaMenuGrid(),
                    children: columns.map((columnLinks, colIndex)=>/*#__PURE__*/ _jsx("div", {
                            className: megaMenuCol(),
                            children: /*#__PURE__*/ _jsx("ul", {
                                className: megaMenuColItems(),
                                children: columnLinks.map((linkItem, linkIndex)=>renderLinkItem(linkItem, linkItem.link?.jsonValue?.value?.text || linkItem.name, categoryTitle, `header-mega-menu-link-${colIndex}-${linkIndex}`, linkItem.name || linkIndex))
                            })
                        }, `column-${colIndex}`))
                })
            ]
        });
    }
    // megaMenuLinks are tier 3 column headers, each containing megaMenuCategory links
    const columnGroups = megaMenuLinks || [];
    return /*#__PURE__*/ _jsx("div", {
        className: megaMenuWrapper(),
        children: /*#__PURE__*/ _jsx("div", {
            className: megaMenuGrid(),
            children: columnGroups?.map((columnGroup, colIndex)=>{
                const columnTitle = getColumnGroupHeading(columnGroup).text;
                const categoryLinks = columnGroup?.megaMenuCategory?.targetItems || [];
                return /*#__PURE__*/ _jsxs("div", {
                    className: megaMenuCol(),
                    children: [
                        renderGroupHeading(columnGroup, '', `header-mega-menu-group-heading-${colIndex}`),
                        /*#__PURE__*/ _jsx("ul", {
                            className: megaMenuColItems(),
                            children: categoryLinks?.map((linkItem, linkIndex)=>renderLinkItem(linkItem, linkItem.name, columnTitle, `header-mega-menu-link-${colIndex}-${linkIndex}`, linkItem.name || linkIndex))
                        })
                    ]
                }, columnGroup.name || colIndex);
            })
        })
    });
};
/** `isOpen` is this item's own dropdown state, so the panel can re-seed itself on close. */ const DropdownMenu = (props)=>{
    const { isOpen } = props;
    // Unwrapped once here so everything below keeps working with ordinary Sitecore fields.
    const navigationTitle = props?.navigationTitle?.jsonValue;
    const navigationLink = props?.navigationLink?.jsonValue;
    const featureTitle = props?.featureTitle?.jsonValue;
    const featureDescription = props?.featureDescription?.jsonValue;
    const megaMenuList = props?.megaMenuList?.targetItems;
    const { getDictionaryValue } = useDictionary();
    const { getActiveTier2Index, asPath } = useHeader();
    const initialSidebarItem = useMemo(()=>{
        const matched = getActiveTier2Index(megaMenuList);
        return matched >= 0 ? matched : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        megaMenuList,
        asPath
    ]);
    const [activeSidebarItem, setActiveSidebarItem] = useState(initialSidebarItem);
    useEffect(()=>{
        if (!isOpen) setActiveSidebarItem(initialSidebarItem);
    }, [
        isOpen,
        initialSidebarItem
    ]);
    // Hide sidebar if there's only one menu group
    const showSidebar = megaMenuList && megaMenuList.length > 1;
    const { dropDownMenuWrapper, dropDownMenuInner, dropDownMenuContainer } = TAILWIND_VARIANTS({
        showSidebar
    });
    const handleSidebarItemClick = (index)=>{
        setActiveSidebarItem(index);
    };
    // Create a derived list that ensures CTA text is populated without mutating props
    const normalizedMegaMenuList = useMemo(()=>{
        if (!megaMenuList) return megaMenuList;
        const fallbackText = getDictionaryValue('CategoryCTAName');
        return megaMenuList.map((group)=>{
            const cta = group.megaMenuCTA?.jsonValue;
            if (cta?.value?.href && !cta.value.text) {
                return {
                    ...group,
                    // Rebuilt wrapped, so the rest of the tree keeps reading `megaMenuCTA.jsonValue`.
                    megaMenuCTA: {
                        jsonValue: {
                            ...cta,
                            value: {
                                ...cta.value,
                                text: fallbackText
                            }
                        }
                    }
                };
            }
            return group;
        });
    }, [
        megaMenuList,
        getDictionaryValue
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: dropDownMenuWrapper(),
        "data-mega-menu": "true",
        children: /*#__PURE__*/ _jsx("div", {
            className: dropDownMenuInner(),
            children: /*#__PURE__*/ _jsx("div", {
                className: dropDownMenuContainer(),
                children: navigationTitle && megaMenuList && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        showSidebar && /*#__PURE__*/ _jsx(SidebarNavigation, {
                            navigationTitle: navigationTitle,
                            navigationLink: navigationLink,
                            megaMenuList: normalizedMegaMenuList,
                            activeSidebarItem: activeSidebarItem,
                            onSidebarItemClick: handleSidebarItemClick,
                            featureTitle: featureTitle,
                            featureDescription: featureDescription
                        }),
                        /*#__PURE__*/ _jsx(MegaMenuContent, {
                            megaMenuList: normalizedMegaMenuList,
                            activeSidebarItem: activeSidebarItem,
                            showSidebar: showSidebar,
                            navigationTitle: navigationTitle,
                            navigationLink: navigationLink
                        })
                    ]
                })
            })
        })
    });
};
const NavLink = ({ link, name })=>{
    const isExternal = link?.value?.target === '_blank';
    const { svgIconClass, linkContent } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsxs("span", {
        className: linkContent(),
        children: [
            name || link?.value?.text,
            !isExternal && /*#__PURE__*/ _jsx(SvgIcon, {
                className: svgIconClass(),
                icon: "arrow-dash-right",
                size: "xs",
                viewBox: "0 0 20 20"
            })
        ]
    });
};
export const Logo = ({ logo, logoLink })=>{
    const { logoContainer, logoLink: logoLinkClass } = TAILWIND_VARIANTS();
    const image = /*#__PURE__*/ _jsx(ImageWrapper, {
        field: logo,
        priority: true
    });
    if (!logoLink?.value?.href) {
        return /*#__PURE__*/ _jsx("div", {
            className: logoContainer(),
            ...getTestProps(`header-logo`),
            children: image
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: logoContainer(),
        children: /*#__PURE__*/ _jsx(LinkWrapper, {
            ctaComponentClass: "default",
            className: logoLinkClass(),
            field: logoLink?.value,
            onClick: (e)=>{
                e.preventDefault();
                const href = logoLink?.value?.href;
                if (href) {
                    window.location.href = href;
                }
            },
            suppressNewTabIcon: true,
            "aria-label": process.env.SITECORE_SITE_NAME,
            ...getTestProps(`header-logo`),
            children: image
        })
    });
};
const NavItem = (props)=>{
    const { dropdownOpen, index } = props || {};
    const megaMenuList = props?.megaMenuList?.targetItems ?? [];
    const navigationLink = props?.navigationLink?.jsonValue;
    const navigationTitle = props?.navigationTitle?.jsonValue;
    const isList = megaMenuList?.length > 0;
    const navigationLinks = navigationLink?.value?.href;
    const { isTier1Active, asPath } = useHeader();
    /*
   * State
   */ // Use useMemo to recalculate when megaMenuList data or path changes
    const isActive = useMemo(()=>{
        return isTier1Active(navigationLinks, megaMenuList, isList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        navigationLinks,
        megaMenuList,
        isList,
        asPath
    ]);
    const megaNavRef = useRef(null);
    const toggleButtonRef = useRef(null);
    const menuItemRef = useRef(null);
    /*
   * EVENT HANDLERS
   */ const handleOnBlur = (event)=>{
        if (!isList) return;
        const nextFocus = event.relatedTarget;
        if (nextFocus?.closest?.('[data-mega-menu="true"]')) return;
        if (lastPointerDown && isPointInsideOpenMegaMenu(lastPointerDown.x, lastPointerDown.y)) {
            return;
        }
        // Only close if we have a relatedTarget and it's outside the current menu item
        if (nextFocus !== null && !event.currentTarget.contains(nextFocus)) {
            props?.close();
        }
    };
    const toggleDropdown = ()=>{
        if (isOpenForThisItem) {
            props?.close();
        } else {
            props?.open();
        }
    };
    const openAndFocusSidebar = (edge)=>{
        props.open();
        setTimeout(()=>{
            const dropdown = document.getElementById(`header-dropdown-${index}`);
            if (!dropdown) return;
            const items = Array.from(dropdown.querySelectorAll('[data-mega-menu-sidebar-item]'));
            if (!items.length) return;
            const selected = dropdown.querySelector('[data-mega-menu-sidebar-selected]');
            const fallback = edge === 'last' ? items[items.length - 1] : items[0];
            (selected ?? fallback).focus();
        }, 0);
    };
    const handleEscape = (event)=>{
        if (event.key !== 'Escape' || !isOpenForThisItem) return;
        event.preventDefault();
        props.close();
        toggleButtonRef.current?.focus();
    };
    const handleKeyDown = (event)=>{
        // Enter/Space are left to the chevron button's native activation, Escape to the wrapper.
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && isList) {
            event.preventDefault();
            openAndFocusSidebar(event.key === 'ArrowDown' ? 'first' : 'last');
        }
    };
    useClickOutside(menuItemRef, dropdownOpen === index, (event)=>{
        if (dropdownOpen !== index) return;
        const target = event.target;
        if (target?.closest?.('[data-nav-dropdown-toggle]')) return;
        if (isInsideOpenMegaMenu(event)) return;
        props?.close();
    });
    // Skip rendering if no link and not a dropdown
    if (!navigationLinks && !isList) return null;
    /*
   * Rendering
   */ const isOpenForThisItem = isList && index === dropdownOpen;
    const isAnotherMenuOpen = dropdownOpen !== null && dropdownOpen !== index;
    const { buttonItemIcon, buttonItemLabel, dropdownToggleButton, navItemRow, navTitleLinkWrapper, navItemWrapper, dropdownToggleIconButton } = TAILWIND_VARIANTS({
        isActive: isActive && !isAnotherMenuOpen,
        isDropdownOpenForItem: isOpenForThisItem
    });
    const dropdownToggle = props.remixIcon ? /*#__PURE__*/ _jsx("span", {
        className: dropdownToggleIconButton(),
        children: /*#__PURE__*/ _jsx(RemixIcon, {
            className: buttonItemIcon(),
            name: isOpenForThisItem ? props.remixIconOpen ?? 'arrow-up-s-line' : props.remixIcon,
            size: props.remixIconSize ?? 'md'
        })
    }) : null;
    return (
        /*#__PURE__*/ _jsx("div", {
            onBlur: handleOnBlur,
            onKeyDown: handleEscape,
            ref: menuItemRef,
            className: `${navItemWrapper()} ${index === 0 ? 'w-min xl:w-auto' : ''}`,
            children: isList ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: navItemRow(),
                        children: /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            ref: toggleButtonRef,
                            "aria-haspopup": "true",
                            "aria-expanded": isOpenForThisItem,
                            "aria-controls": isOpenForThisItem ? `header-dropdown-${index}` : undefined,
                            "aria-label": `Toggle ${navigationTitle?.value ?? ''} submenu`.replace(/\s+/g, ' ').trim(),
                            className: dropdownToggleButton(),
                            "data-nav-dropdown-toggle": "",
                            onClick: toggleDropdown,
                            onKeyDown: handleKeyDown,
                            ...getTestProps(`header-nav-toggle-${index}`),
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: buttonItemLabel(),
                                    ...getTestProps(`header-nav-label`),
                                    children: index === 0 && navigationTitle?.value?.includes('&') ? /*#__PURE__*/ _jsx(_Fragment, {
                                        children: navigationTitle.value.split(' &').map((part, i, arr)=>i < arr.length - 1 ? /*#__PURE__*/ _jsxs(React.Fragment, {
                                                children: [
                                                    part,
                                                    /*#__PURE__*/ _jsx("wbr", {}),
                                                    " ",
                                                    "&"
                                                ]
                                            }, i) : part)
                                    }) : /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        field: navigationTitle
                                    })
                                }),
                                dropdownToggle
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        id: `header-dropdown-${index}`,
                        ref: megaNavRef,
                        className: index === dropdownOpen ? 'block' : 'hidden',
                        children: /*#__PURE__*/ _jsx(DropdownMenu, {
                            ...props,
                            isOpen: isOpenForThisItem
                        })
                    })
                ]
            }) : /*#__PURE__*/ _jsx(LinkWrapper, {
                ctaComponentClass: "default",
                "aria-haspopup": "false",
                "aria-current": isActive ? 'page' : undefined,
                className: navTitleLinkWrapper(),
                ctaVariant: "custom",
                field: navigationLink,
                suppressNewTabIcon: true,
                gtmEvent: createNavClickGTMEvent({
                    clickText: navigationTitle?.value,
                    navLevel: 1,
                    navLocation: 'header',
                    menuCat1: navigationTitle?.value,
                    linkUrl: navigationLink?.value?.href
                }),
                ...getTestProps(`header-nav-link`),
                children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                    field: navigationTitle
                })
            })
        })
    );
};
const HeaderDesktop = (props)=>{
    const item = props?.fields?.data?.item;
    const logo = item?.logo?.jsonValue;
    const logoLink = item?.logoLink?.jsonValue;
    const contactUsCTA = item?.contactUsCTA?.jsonValue;
    const navigationList = item?.navigationList?.targetItems ?? [];
    const headerRef = useRef(null);
    const menuContainerRef = useRef(null);
    const searchBoxContainerRef = useRef(null);
    const { isDesktopDropdownOpen, showSearch, setDesktopDropdownOpen, setShowSearch, closeDesktopMenus, handleOverlayChange } = useHeader();
    const isTabletDevice = useIsTablet();
    const { getDictionaryValue } = useDictionary();
    /*
   * STATE
   */ const isScrolled = useIsScrolled();
    const isDropdownOpen = isDesktopDropdownOpen !== null || showSearch;
    const isDesktopVisible = !isTabletDevice; // Desktop header is visible when NOT on tablet
    useEffect(()=>{
        const currentHeaderRef = headerRef.current;
        if (!currentHeaderRef) return;
        const handleHeaderClick = (e)=>{
            // Don't close if clicking inside the header and any menu is open
            if (currentHeaderRef.contains(e.target) && isDesktopDropdownOpen !== null) {
                return;
            }
        };
        currentHeaderRef.addEventListener('click', handleHeaderClick);
        return ()=>{
            currentHeaderRef.removeEventListener('click', handleHeaderClick);
        };
    }, [
        isDesktopDropdownOpen
    ]);
    useEffect(()=>{
        if (showSearch && searchBoxContainerRef.current) {
            // Focus the first input inside the search box
            const input = searchBoxContainerRef.current.querySelector('input');
            if (input) {
                input.focus();
                // Add keydown listener for Escape key to close search
                const handleKeydown = (e)=>{
                    if (e.key === 'Escape') {
                        setShowSearch(false);
                    }
                };
                // Store ref in variable to avoid stale closure
                const currentRef = searchBoxContainerRef.current;
                // Add the event listener to the search container
                currentRef.addEventListener('keydown', handleKeydown);
                // Cleanup
                return ()=>{
                    currentRef?.removeEventListener('keydown', handleKeydown);
                };
            }
            // Explicitly return undefined if input is not found
            return undefined;
        }
        // Explicitly return undefined if showSearch is false or ref is not set
        return undefined;
    }, [
        showSearch,
        setShowSearch
    ]);
    /*
   * EVENT HANDLERS
   */ const handleDropdownToggle = useCallback((index)=>{
        // Close any open menus
        if (index === null) {
            setDesktopDropdownOpen(null);
            setShowSearch(false);
            return;
        }
        setDesktopDropdownOpen(index);
        setShowSearch(false);
        handleOverlayChange();
    }, [
        setDesktopDropdownOpen,
        setShowSearch,
        handleOverlayChange
    ]);
    /*
   * LIFECYCLE
   */ useClickOutside(headerRef, isDesktopVisible && (isDropdownOpen || showSearch), (event)=>{
        // Pressing anywhere on the open panel keeps it open; only the page outside it closes.
        if (isInsideOpenMegaMenu(event)) return;
        closeDesktopMenus();
    });
    useOnRouteChange(closeDesktopMenus);
    /*
   * Rendering
   */ if (!item) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const { base, wrapper, inner, container, menuContainer, menuWrapper, menuItems, languageWrapper, searchBoxWrapper, searchBox, desktopSearchButton, desktopSearchIcon, ctaButton } = TAILWIND_VARIANTS({
        isDropdownOpen: isDropdownOpen,
        isScrolled: isScrolled,
        showSearch: showSearch
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: base(),
            id: "header-desktop",
            ref: headerRef,
            ...getTestProps(`header-desktop`),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: wrapper(),
                    children: /*#__PURE__*/ _jsx("div", {
                        className: inner(),
                        children: /*#__PURE__*/ _jsx("div", {
                            className: container(),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: menuWrapper(),
                                children: [
                                    /*#__PURE__*/ _jsx(Logo, {
                                        logo: logo,
                                        logoLink: logoLink
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: menuContainer(),
                                        ref: menuContainerRef,
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: menuItems(),
                                            role: "navigation",
                                            "aria-label": "Main navigation",
                                            children: navigationList?.map((item, index)=>/*#__PURE__*/ _jsx(NavItem, {
                                                    dropdownOpen: isDesktopDropdownOpen,
                                                    index: index,
                                                    headerRef: headerRef,
                                                    open: ()=>{
                                                        handleDropdownToggle(index);
                                                        setShowSearch(false);
                                                    },
                                                    close: ()=>{
                                                        handleDropdownToggle(null);
                                                        setShowSearch(false);
                                                    },
                                                    remixIcon: "arrow-down-s-line",
                                                    ...item
                                                }, item?.name))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: languageWrapper(),
                                        children: [
                                            /*#__PURE__*/ _jsx("button", {
                                                className: desktopSearchButton(),
                                                "aria-expanded": !!showSearch,
                                                "aria-label": "Toggle Search",
                                                "aria-haspopup": "dialog",
                                                "aria-controls": showSearch ? 'header-search-dialog' : undefined,
                                                onClick: ()=>setShowSearch(!showSearch),
                                                ...getTestProps(`header-search-button`),
                                                children: /*#__PURE__*/ _jsx(SvgIcon, {
                                                    icon: "outline-search",
                                                    fill: "none",
                                                    size: "s",
                                                    viewBox: "0 0 21 21",
                                                    className: desktopSearchIcon()
                                                })
                                            }),
                                            contactUsCTA?.value?.href && /*#__PURE__*/ _jsx(LinkWrapper, {
                                                ctaComponentClass: "default",
                                                className: ctaButton(),
                                                field: contactUsCTA,
                                                suppressNewTabIcon: true,
                                                ...getTestProps(`header-cta-button`),
                                                gtmEvent: createCTAClickGTMEvent({
                                                    header: 'N/A',
                                                    ctaText: contactUsCTA.value.text,
                                                    ctaType: 'blue',
                                                    componentName: 'Header CTA',
                                                    ctaLink: contactUsCTA?.value?.href
                                                }),
                                                children: contactUsCTA.value.text || 'Contact Us'
                                            })
                                        ]
                                    })
                                ]
                            })
                        })
                    })
                }),
                showSearch && /*#__PURE__*/ _jsx("div", {
                    id: "header-search-dialog",
                    className: searchBoxWrapper(),
                    ref: searchBoxContainerRef,
                    ...getTestProps(`header-search-box`),
                    children: /*#__PURE__*/ _jsx("div", {
                        className: searchBox(),
                        children: /*#__PURE__*/ _jsx(GlobalSearchInput, {
                            placeholder: getDictionaryValue('SearchKeywords') || 'Search Keywords',
                            onSearch: ()=>{
                                setShowSearch(false);
                            },
                            onClear: ()=>{
                            // Search cleared
                            }
                        })
                    })
                })
            ]
        })
    });
};
export default HeaderDesktop;
const TAILWIND_VARIANTS = tv({
    slots: {
        base: 'bg-color-core-color-1-Navy',
        navItemRow: [
            'inline-flex',
            'items-center',
            'gap-1',
            'rounded-general-border-radius-card',
            'text-component-header-nav-link-text',
            'text-center',
            'whitespace-nowrap',
            'relative',
            'group',
            'transition-all',
            'duration-200',
            'hover:bg-color-text-white',
            'hover:text-color-core-color-1-Navy',
            'has-[:focus-visible]:outline',
            'has-[:focus-visible]:outline-[length:var(--border-width-width-05)]',
            'has-[:focus-visible]:outline-color-border-border-focus',
            'has-[:focus-visible]:bg-color-text-white',
            'has-[:focus-visible]:text-color-core-color-1-Navy'
        ],
        buttonItemLabel: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-inherit',
            'hover:no-underline',
            'focus:no-underline'
        ],
        dropdownToggleButton: [
            'inline-flex',
            'items-center',
            'justify-center',
            'rounded-general-border-radius-card',
            'focus-visible:outline-none',
            'px-4',
            'xl:px-4',
            'py-3'
        ],
        dropdownToggleIconButton: [
            'inline-flex',
            'items-center',
            'justify-center',
            'rounded-general-border-radius-card',
            'focus-visible:outline-none',
            'py-0'
        ],
        buttonItemIcon: [
            'duration-200',
            'h-auto',
            'transition',
            '!w-xs',
            'align-middle',
            'text-component-header-nav-link-icon'
        ],
        buttonItemSublink: [
            'flex',
            'gap-2',
            'items-center'
        ],
        container: [
            'max-w-screen-dimensions-max-width',
            'min-w-screen-dimensions-min-width',
            'w-full'
        ],
        dropDownMenuCol: [
            'flex',
            'flex-col',
            'gap-4',
            'p-4'
        ],
        dropDownMenuColHeading: [
            'font-semibold',
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary',
            'text-sm'
        ],
        dropDownMenuColItems: [
            'flex',
            'flex-col',
            'gap-4',
            'list-none'
        ],
        dropDownMenuColItemsLink: [
            'text-color-text-text',
            'hover:underline',
            'group',
            'flex',
            'items-center',
            'justify-between'
        ],
        dropDownMenuGrid: [
            'grid',
            'w-full',
            'grid-cols-1',
            'md:grid-cols-2',
            'lg:grid-cols-3',
            'xl:grid-cols-4',
            'gap-8',
            'p-8'
        ],
        dropDownMenuInner: [
            'bg-color-surface-surface',
            'overflow-hidden',
            'rounded-component-header-nav-dropdown-border-radius',
            'mx-spacing-spacing-24'
        ],
        dropDownMenuSection: [
            'max-w-screen-dimensions-max-width',
            'min-w-screen-dimensions-min-width',
            'lg:px-general-spacing-margin-x',
            'flex',
            'xl:mx-auto'
        ],
        dropDownMenuWrapper: [
            'max-w-screen-dimensions-max-width',
            'absolute',
            '-mt-2',
            'pt-[14px]',
            'mx-auto',
            'left-0',
            'right-0',
            'z-10',
            'top-full',
            'max-h-[calc(100vh-100px)]',
            'gap-spacing-spacing-32'
        ],
        dropDownMenuContainer: [
            'max-w-screen-dimensions-max-width',
            'min-w-screen-dimensions-min-width',
            'gap-spacing-spacing-32',
            'flex',
            'xl:mx-auto'
        ],
        sidebarWrapper: [
            'w-1/4',
            'lg:w-[30%]',
            'xl:w-1/4',
            'bg-color-bg-light',
            'flex',
            'flex-col',
            'max-h-[calc(100vh-100px)]',
            'overflow-hidden'
        ],
        sidebarTitleLink: [
            'text-component-header-mega-menu-link-text-onWhite',
            'font-inherit',
            'w-fit',
            '!p-0',
            '!gap-0',
            'hover:underline',
            'focus-visible:outline',
            'focus-visible:outline-2',
            'focus-visible:outline-color-core-color-4-SkyBlue'
        ],
        sidebarTitleText: [
            'text-component-header-mega-menu-link-text-onWhite',
            'font-inherit',
            'w-fit'
        ],
        sidebarTitle: [
            'text-component-header-mega-menu-category-label-onDark',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'font-normal',
            'leading-typography-line-height-body-large',
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'mb-spacing-spacing-24',
            'p-spacing-spacing-32',
            'pb-0',
            'sticky',
            'top-0',
            'bg-color-bg-light',
            'z-10'
        ],
        sidebarDivider: [
            'w-[100px]',
            'h-[2px]',
            'bg-color-core-color-4-SkyBlue'
        ],
        sidebarScrollArea: [
            'flex-1',
            'min-h-0',
            'overflow-y-auto',
            // Custom scrollbar styling
            '[&::-webkit-scrollbar]:w-2',
            '[&::-webkit-scrollbar-track]:bg-white/15',
            '[&::-webkit-scrollbar-track]:rounded-full',
            '[&::-webkit-scrollbar-thumb]:bg-white/50',
            '[&::-webkit-scrollbar-thumb]:rounded-full',
            '[&::-webkit-scrollbar-thumb:hover]:bg-white/60',
            // Firefox scrollbar
            'scrollbar-thin',
            'scrollbar-track-white/15',
            'scrollbar-thumb-white/50',
            'scrollbar-thumb-rounded'
        ],
        sidebarList: [
            'flex',
            'flex-col',
            'list-none',
            'gap-[6px]',
            'pl-spacing-spacing-32',
            'pr-spacing-spacing-24'
        ],
        sidebarLinks: [
            'group',
            'mt-0.5',
            'flex',
            'flex-col',
            'text-component-header-mega-menu-link-text-onWhite',
            'rounded-component-header-nav-category-border-radius',
            'hover:bg-color-black-alpha-10',
            'hover:text-component-header-mega-menu-link-text-onWhite',
            // Keyboard only, same reasoning as the main bar above.
            'has-[:focus-visible]:outline',
            'has-[:focus-visible]:outline-[length:var(--border-width-width-05)]',
            'has-[:focus-visible]:outline-color-border-border-focus'
        ],
        sidebarItem: [
            // 'text-component-header-mega-menu-link-text-onWhite',
            'p-spacing-spacing-8',
            // Matches the row, so a background or focus ring on the control cannot square it off.
            'rounded-component-header-nav-category-border-radius',
            'text-inherit',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-left',
            'w-full',
            'flex',
            'items-center',
            'justify-between',
            'hover:no-underline',
            'focus:bg-transparent',
            'focus:outline-none',
            'focus:outline-0',
            'focus:ring-transparent',
            'focus:no-underline',
            'hover:text-component-header-mega-menu-link-text-onWhite'
        ],
        sidebarItemActive: [
            'text-component-header-mega-menu-link-text-hover-onDark',
            'hover:!text-component-header-mega-menu-link-text-hover-onDark'
        ],
        sidebarLinksActive: [
            '!bg-color-bg-dark',
            '!text-component-header-mega-menu-link-text-hover-onDark'
        ],
        sidebarItemIcon: [
            '!w-5',
            'opacity-100',
            'text-p-color-icon',
            'group-hover:text-component-header-mega-menu-link-text-onWhite'
        ],
        sidebarItemDescription: [
            // 'text-color-text-white',
            'font-typography-font-family-body',
            'text-typography-font-size-body-small',
            'font-normal',
            'mr-[28px]'
        ],
        sidebarItemDescriptionText: [
            'typography-font-family-body',
            'font-normal'
        ],
        sidebarItemDivider: [
            'w-full',
            'h-[1px]',
            'bg-color-core-color-4-SkyBlue'
        ],
        sidebarFeatureWrapper: [
            'pt-spacing-spacing-24',
            'px-spacing-spacing-32',
            'pb-spacing-spacing-32',
            'flex',
            'flex-col',
            'bg-color-bg-light',
            'flex-shrink-0'
        ],
        sidebarFeatureDivider: [
            'w-full',
            'h-[1px]',
            'bg-color-core-color-4-SkyBlue',
            'mb-spacing-spacing-32'
        ],
        sidebarFeatureTitle: [
            'text-color-text-white',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-bold',
            'leading-typography-line-height-body-medium'
        ],
        megaMenuWrapper: [
            'flex-1',
            'p-6',
            'bg-color-surface-surface',
            'overflow-y-auto',
            'max-h-[calc(100vh-100px)]',
            // Custom scrollbar styling
            '[&::-webkit-scrollbar]:w-2',
            '[&::-webkit-scrollbar-track]:bg-gray-400',
            '[&::-webkit-scrollbar-track]:rounded-full',
            '[&::-webkit-scrollbar-thumb]:bg-gray-600',
            '[&::-webkit-scrollbar-thumb]:rounded-full',
            '[&::-webkit-scrollbar-thumb:hover]:bg-gray-800',
            // Firefox scrollbar
            'scrollbar-thin',
            'scrollbar-track-gray-400',
            'scrollbar-thumb-gray-600'
        ],
        megaMenuDivider: [
            'w-[100px]',
            'h-[2px]',
            'bg-color-core-color-4-SkyBlue'
        ],
        megaMenuGrid: [
            'grid',
            'grid-flow-col',
            'auto-cols-fr',
            'gap-4'
        ],
        megaMenuCol: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        megaMenuColHeading: [
            'text-color-text-text',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'font-normal',
            'leading-typography-line-height-body-large',
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        megaMenuColHeadingLink: [
            'text-inherit',
            'font-inherit',
            'w-fit',
            '!p-0',
            '!gap-0',
            'hover:underline',
            'focus-visible:outline',
            'focus-visible:outline-2',
            'focus-visible:outline-color-core-color-4-SkyBlue',
            'focus-visible:rounded-component-header-nav-category-border-radius'
        ],
        megaMenuColItems: [
            'flex',
            'flex-col',
            'gap-2',
            'list-none'
        ],
        megaMenuColItemsLink: [
            'text-color-text-text',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'hover:underline',
            'group',
            'inline-flex',
            'items-center',
            'gap-spacing-spacing-8',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'focus:rounded-component-header-nav-category-border-radius',
            'focus:bg-transparent',
            'focus:!shadow-none',
            'focus:!ring-0',
            'focus:!no-underline'
        ],
        dropDownFeaturedWrapper: [
            'w-full',
            'max-w-sm',
            'bg-color-surface-surface-secondary',
            'py-spacing-spacing-24',
            'pl-spacing-spacing-24',
            'pr-spacing-spacing-80',
            'gap-spacing-spacing-24',
            'flex',
            'flex-col',
            'gap-1',
            'p-4',
            'relative',
            'before:absolute',
            'before:top-0',
            'before:left-full',
            'before:content-[""]',
            'before:bg-color-surface-surface-secondary',
            'before:h-full',
            'before:w-screen'
        ],
        dropDownFeaturedContent: [
            'flex',
            'flex-col'
        ],
        dropDownFeaturedTitle: [
            'font-typography-font-weight-medium',
            'text-typography-font-size-header-xSmall'
        ],
        dropDownFeaturedDescription: [
            'font-typography-font-weight-body',
            'text-typography-font-size-body-small',
            'text-color-text-text-secondary',
            'text-sm'
        ],
        dropDownFeaturedImg: [
            'h-auto',
            'rounded-lg'
        ],
        svgIconClass: [
            'scale-x-0',
            '!h-5',
            '!w-5',
            'hover:text-component-header-mega-menu-link-icon-hover'
        ],
        inner: [
            'flex',
            'justify-center',
            'transition-all',
            'duration-200'
        ],
        languageWrapper: [
            'flex',
            'items-center',
            'justify-end',
            'gap-spacing-spacing-8',
            'flex-shrink-0'
        ],
        logoContainer: [
            'flex',
            'items-center',
            'flex-shrink-0'
        ],
        logoLink: [
            'rounded-border-radius-radius-1',
            'overflow-hidden',
            'focus:outline',
            'focus:outline-2',
            'focus:bg-transparent',
            'focus:outline-color-core-color-4-SkyBlue',
            'max-w-[159px]'
        ],
        menuContainer: [
            'flex',
            'flex-1',
            'justify-center',
            'items-center'
        ],
        menuItems: [
            'flex',
            'items-center',
            'px-0',
            'xl:gap-4'
        ],
        menuWrapper: [
            'flex',
            'justify-between',
            'items-center',
            'gap-4',
            'px-spacing-spacing-24'
        ],
        navAnimation: [
            'absolute',
            'inset-x-0',
            'bottom-0',
            'h-0.5',
            'bg-color-text-white',
            'transform',
            'origin-left',
            'scale-x-0',
            'transition-transform',
            'duration-300',
            'ease-out'
        ],
        navActiveStrokes: [
            'absolute',
            'inset-x-0',
            'bottom-0',
            'h-0.5',
            'bg-color-text-white'
        ],
        navItemWrapper: [],
        navTitleLinkWrapper: [
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-semibold',
            'leading-[22px]',
            'text-component-header-nav-link-text',
            'text-center',
            'whitespace-nowrap',
            'relative',
            'group',
            'px-6',
            'py-3',
            'rounded-general-border-radius-card',
            'transition-all',
            'duration-200',
            'hover:bg-color-text-white',
            'hover:text-color-core-color-1-Navy',
            'focus:bg-color-text-white',
            'focus:text-color-core-color-1-Navy',
            'focus:outline',
            'focus:outline-[length:var(--border-width-width-05)]',
            'focus:outline-color-border-border-focus',
            'focus-visible:outline-offset-0'
        ],
        searchBoxWrapper: [
            'bg-color-core-color-1-Navy',
            'px-0',
            'py-4',
            'border-b',
            'border-color-border-border'
        ],
        searchBox: [
            'm-auto',
            'max-w-screen-sm'
        ],
        desktopSearchButton: [
            'flex',
            'w-12',
            'h-12',
            'p-spacing-spacing-12',
            'justify-center',
            'items-center',
            'gap-spacing-spacing-8',
            'aspect-square',
            'rounded-general-border-radius-button',
            'border',
            'border-component-button-secondary-border',
            'bg-component-button-secondary-inverse-bg',
            'hover:bg-component-button-secondary-inverse-bg-hover',
            'focus:bg-component-button-secondary-inverse-bg-hover',
            'transition-colors',
            'duration-200',
            'group',
            'focus-visible:outline-offset-0',
            'focus-visible:outline'
        ],
        desktopSearchIcon: [
            'text-component-button-secondary-border'
        ],
        wrapper: [
            'border-color-border-border',
            'border-b'
        ],
        ctaButton: [
            'bg-color-core-color-4-SkyBlue',
            'text-color-text-white',
            'px-spacing-spacing-24',
            'py-spacing-spacing-12',
            'rounded-lg',
            'font-typography-font-family-body',
            'font-semibold',
            'text-typography-font-size-body-medium',
            'hover:bg-color-text-white',
            'hover:text-color-core-color-1-Navy',
            'hover:no-underline',
            'focus:no-underline',
            'transition-all',
            'duration-200',
            'focus:bg-white',
            'focus:text-color-core-color-1-Navy',
            'focus:outline',
            'focus:outline-2',
            'focus:outline-color-core-color-4-SkyBlue',
            'focus:!ring-0',
            'focus:!ring-offset-0',
            'focus:!shadow-none',
            'focus:!outline-offset-0'
        ],
        featuredLink: [
            'py-2',
            'text-color-text-text',
            'hover:underline',
            'flex',
            'items-center',
            'justify-between',
            'group'
        ],
        linkContent: [
            'flex',
            'items-center'
        ]
    },
    variants: {
        isActive: {
            false: {},
            true: {
                navItemRow: [
                    'bg-color-text-white',
                    '!text-color-core-color-1-Navy'
                ],
                navTitleLinkWrapper: [
                    'bg-color-text-white',
                    '!text-color-core-color-1-Navy'
                ],
                buttonItemSublink: [
                    '!text-color-core-color-1-Navy'
                ]
            }
        },
        isDropdownOpen: {
            false: {
                wrapper: [
                    'border-b'
                ]
            }
        },
        isDropdownOpenForItem: {
            false: {},
            true: {
                navItemRow: [
                    'bg-color-text-white',
                    '!text-color-core-color-1-Navy',
                    'rounded-general-border-radius-card'
                ],
                buttonItemSublink: [
                    '!text-color-core-color-1-Navy'
                ]
            }
        },
        isScrolled: {
            false: {
                inner: [
                    'py-2'
                ],
                divider: [
                    'h-3'
                ],
                dropDownMenuInner: [
                    'border-color-border-light'
                ]
            },
            true: {
                inner: [
                    'py-3'
                ],
                divider: [
                    'h-2'
                ],
                dropDownMenuInner: [
                    'border-color-border-light'
                ]
            }
        },
        showSidebar: {
            false: {
                megaMenuWrapper: [
                    'flex-1',
                    'p-6',
                    'bg-color-surface-surface',
                    'w-full'
                ]
            },
            true: {}
        },
        showSearch: {
            false: {},
            true: {
                desktopSearchButton: [
                    'bg-component-button-secondary-inverse-bg-hover'
                ],
                wrapper: [
                    '!border-b-0'
                ]
            }
        }
    }
});
